import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, KeyRound, CheckCircle2, AlertTriangle,
  Copy, Eye, EyeOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

// iter131 SEC-001 closer — Admin password rotation UI. Same pattern
// as /admin/quick-upgrade. Two modes: rotate an existing admin, or
// create a brand-new admin. The plaintext password is shown ONCE on
// success and never re-fetchable — the admin has to copy it before
// leaving the page. Route: /admin/rotate-password (admin-only via
// backend 403).

export default function AdminRotatePassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [action, setAction] = useState("rotate");   // "rotate" | "create"
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);       // { action, email, password, self_rotated }
  const [error, setError] = useState(null);         // { headline, body }
  const [reveal, setReveal] = useState(true);       // show password by default; user can hide

  const canSubmit = email.trim().length > 3 && confirmChecked && !busy && !result;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/rotate-admin-password", {
        email: email.trim(),
        action,
      });
      setResult(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      if (status === 403) {
        setError({ headline: "Admins only", body: "This page is admin-only." });
      } else if (status === 404) {
        setError({ headline: "User not found", body: detail });
      } else if (status === 409) {
        setError({ headline: "User already exists", body: detail });
      } else if (status === 400) {
        setError({ headline: "Invalid input", body: detail });
      } else {
        setError({ headline: "Couldn't complete the action", body: detail || "Please try again." });
      }
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.password) return;
    try {
      await navigator.clipboard.writeText(result.password);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  const dismissResult = () => {
    // Wipe from state entirely so it can't be re-read from a debugger.
    setResult(null);
    setEmail("");
    setConfirmChecked(false);
    setReveal(true);
    // If they rotated their own account, their JWT is now invalid —
    // send them to /login so the next call doesn't 401 in a confusing
    // place.
    if (result?.self_rotated) {
      toast.info("You rotated your own account — please sign in again");
      // Local logout: clear token, hard nav.
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  return (
    <div data-testid="admin-rotate-password-page" className="space-y-6 max-w-2xl">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--bt-text-muted)] hover:text-[var(--bt-text)] transition-colors"
        data-testid="admin-rotate-password-back"
      >
        <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
      </Link>

      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <KeyRound className="w-6 h-6 text-[var(--bt-accent)]" />
          <p className="bt-label">Admin · sensitive</p>
        </div>
        <h1 className="font-heading text-3xl">Rotate or Create Admin Password</h1>
        <p className="text-sm text-[var(--bt-text-muted)]">
          Generate a fresh 32-char random password for an existing
          admin (rotate) or create a new admin account (create). The
          new password is shown once, never stored, and can't be
          retrieved afterwards. All actions are audit-logged.
        </p>
      </header>

      {/* Form — hidden after success so the password remains the focus */}
      {!result && (
        <section
          data-testid="admin-rotate-password-form"
          className="border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-6 space-y-5"
        >
          <div>
            <label className="bt-label block mb-1.5">Action</label>
            <div className="flex gap-2" data-testid="admin-rotate-password-action-group">
              <button
                type="button"
                onClick={() => setAction("rotate")}
                className={`px-4 py-2 border rounded-sm text-sm transition-colors ${
                  action === "rotate"
                    ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                    : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:border-[var(--bt-text-muted)]"
                }`}
                data-testid="admin-rotate-password-action-rotate"
              >
                Rotate existing
              </button>
              <button
                type="button"
                onClick={() => setAction("create")}
                className={`px-4 py-2 border rounded-sm text-sm transition-colors ${
                  action === "create"
                    ? "border-[var(--bt-accent)] bg-[var(--bt-accent)]/10 text-[var(--bt-text)]"
                    : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:border-[var(--bt-text-muted)]"
                }`}
                data-testid="admin-rotate-password-action-create"
              >
                Create new admin
              </button>
            </div>
            <p className="text-xs text-[var(--bt-text-muted)] mt-1.5">
              {action === "rotate"
                ? "Generates a fresh password for an EXISTING admin account. Their existing sessions will be signed out."
                : "Inserts a brand-new admin account (role=admin, tier=elite). Fails if the email is already in use."}
            </p>
          </div>

          <div>
            <label className="bt-label block mb-1.5">
              {action === "rotate" ? "Target admin email" : "New admin email"}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={action === "rotate" ? "admin@braintrades.com" : "admin@braintrades.ai"}
              className="w-full px-3 py-2 border border-[var(--bt-border)] rounded-sm bg-[var(--bt-bg)] text-sm focus:outline-none focus:border-[var(--bt-accent)]"
              data-testid="admin-rotate-password-email"
              autoFocus
            />
          </div>

          <label
            className="flex items-start gap-2 cursor-pointer select-none"
            data-testid="admin-rotate-password-confirm-label"
          >
            <input
              type="checkbox"
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              className="mt-0.5"
              data-testid="admin-rotate-password-confirm"
            />
            <span className="text-sm text-[var(--bt-text-muted)]">
              I understand the new password will be shown{" "}
              <strong className="text-[var(--bt-text)]">once</strong>{" "}
              and cannot be retrieved afterwards. I will copy it to a
              password manager before leaving this page.
            </span>
          </label>

          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={!canSubmit}
              data-testid="admin-rotate-password-submit"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Working…</>
              ) : (
                <>{action === "rotate" ? "Rotate password" : "Create admin"}</>
              )}
            </Button>
          </div>
        </section>
      )}

      {/* Success — the ONE-TIME password reveal */}
      {result && (
        <section
          data-testid="admin-rotate-password-result"
          className="border-2 border-[var(--bt-win)]/50 bg-[var(--bt-win)]/5 rounded-sm p-6 space-y-4"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" style={{ color: "var(--bt-win)" }} />
            <p className="font-medium">
              Admin {result.action}: <span className="font-mono">{result.email}</span>
            </p>
          </div>

          <div className="border border-[var(--bt-border)] bg-[var(--bt-bg)] rounded-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="bt-label">New password (shown once)</p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setReveal((r) => !r)}
                  className="p-1.5 hover:bg-[var(--bt-surface)] rounded-sm transition-colors"
                  aria-label={reveal ? "Hide password" : "Show password"}
                  data-testid="admin-rotate-password-reveal-toggle"
                >
                  {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="p-1.5 hover:bg-[var(--bt-surface)] rounded-sm transition-colors"
                  aria-label="Copy password"
                  data-testid="admin-rotate-password-copy"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p
              className="font-mono text-lg break-all select-all"
              data-testid="admin-rotate-password-value"
            >
              {reveal
                ? result.password
                : "•".repeat(result.password.length)}
            </p>
          </div>

          <div className="flex items-start gap-2 text-sm text-[var(--bt-text-muted)]">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--bt-loss)" }} />
            <p>
              This is the <strong className="text-[var(--bt-text)]">only</strong>{" "}
              time this password will be displayed. Save it to your
              password manager <strong className="text-[var(--bt-text)]">now</strong>.
              If you lose it, you'll need to run this action again to
              generate a new one.
              {result.self_rotated && (
                <> You rotated your own account — dismissing this
                will sign you out and take you to the login screen.</>
              )}
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={dismissResult}
              data-testid="admin-rotate-password-dismiss"
            >
              I've saved it — dismiss
            </Button>
          </div>
        </section>
      )}

      {error && (
        <section
          data-testid="admin-rotate-password-error"
          className="border border-[var(--bt-loss)]/40 bg-[var(--bt-loss)]/5 rounded-sm p-5 space-y-1"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
            <p className="font-medium">{error.headline}</p>
          </div>
          <p className="text-sm text-[var(--bt-text-muted)]">{error.body}</p>
        </section>
      )}
    </div>
  );
}
